import React, { useState, useEffect, useRef } from "react";
import { MapPin, Loader2, X } from "lucide-react";
import { useDebounce } from "../hooks/useDebounce";
import { searchCities, CityResult } from "../utils/distanceCalculator";
import { useTranslation } from "react-i18next";

interface CityAutocompleteProps {
  value: string;
  onChange: (value: string, cityData?: CityResult) => void;
  placeholder: string;
  label: string;
  required?: boolean;
  className?: string;
}

const CityAutocomplete: React.FC<CityAutocompleteProps> = ({
  value,
  onChange,
  placeholder,
  label,
  required = false,
  className = "",
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [results, setResults] = useState<CityResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Debounce the search query
  const debouncedQuery = useDebounce(value, 300);

  // Search for cities when debounced query changes
  useEffect(() => {
    const performSearch = async () => {
      if (debouncedQuery.length < 2) {
        setResults([]);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        const cities = await searchCities(debouncedQuery);
        setResults(cities);
      } catch (error) {
        console.error("Search error:", error);
        setResults([]);
      } finally {
        setIsLoading(false);
      }
    };

    performSearch();
  }, [debouncedQuery]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);
    setIsOpen(true);
    setSelectedIndex(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === "ArrowDown" && results.length > 0) {
        setIsOpen(true);
        setSelectedIndex(0);
        e.preventDefault();
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev < results.length - 1 ? prev + 1 : prev
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case "Enter":
        e.preventDefault();
        if (selectedIndex >= 0 && results[selectedIndex]) {
          selectCity(results[selectedIndex]);
        }
        break;
      case "Escape":
        setIsOpen(false);
        setSelectedIndex(-1);
        inputRef.current?.blur();
        break;
    }
  };

  const getDisplayText = (city: CityResult) => {
    const parts = city.display_name.split(",").map((part) => part.trim());
    const mainName = parts[0];

    // For different types of places, show more context
    const addresstype = city.addresstype || city.type;

    if (addresstype === "hamlet" || addresstype === "village") {
      // For small places, show municipality/city as well
      const municipality = city.address?.municipality || city.address?.city;
      return municipality && municipality !== mainName
        ? `${mainName}, ${municipality}`
        : mainName;
    } else if (addresstype === "city_district") {
      // For city districts, show the main city
      const mainCity = city.address?.city;
      return mainCity && mainCity !== mainName
        ? `${mainName} (${mainCity})`
        : mainName;
    } else if (addresstype === "state") {
      // For states, add "Province" or "State" indicator
      return `${mainName} (${t("1162", "Province")})`;
    }

    return mainName;
  };

  const getSubtext = (city: CityResult) => {
    const addresstype = city.addresstype || city.type;

    // Create a meaningful subtext based on place type
    if (addresstype === "city") {
      // For cities, show state/province and country
      const state = city.address?.state;
      const country = city.address?.country;
      return [state, country].filter(Boolean).join(", ");
    } else if (addresstype === "hamlet" || addresstype === "village") {
      // For small places, show municipality, state, country
      const municipality = city.address?.municipality;
      const state = city.address?.state;
      const country = city.address?.country;
      return [municipality, state, country].filter(Boolean).join(", ");
    } else if (addresstype === "city_district") {
      // For districts, show state and country
      const state = city.address?.state;
      const country = city.address?.country;
      return [state, country].filter(Boolean).join(", ");
    } else if (addresstype === "state") {
      // For states, just show country
      return city.address?.country || "";
    }

    // Fallback: everything except the first part
    const parts = city.display_name.split(",").map((part) => part.trim());
    return parts.slice(1).join(", ");
  };

  const getTypeIndicator = (city: CityResult) => {
    const addresstype = city.addresstype || city.type;
    switch (addresstype) {
      case "city":
        return t("1163", "City");
      case "hamlet":
        return t("1164", "Hamlet");
      case "village":
        return t("1165", "Village");
      case "city_district":
        return t("1166", "District");
      case "state":
        return t("1167", "Province");
      case "administrative":
        return t("1168", "Area");
      default:
        return "";
    }
  };

  const selectCity = (city: CityResult) => {
    const displayText = getDisplayText(city);
    onChange(displayText, city);
    setIsOpen(false);
    setSelectedIndex(-1);
  };

  const clearInput = () => {
    onChange("");
    setIsOpen(false);
    setResults([]);
    inputRef.current?.focus();
  };

  return (
    <div className="relative">
      <label className="block text-sm font-medium text-gray-300 mb-1">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>

      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <MapPin className="h-4 w-4 text-gray-400" />
        </div>

        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (results.length > 0) setIsOpen(true);
          }}
          placeholder={placeholder}
          required={required}
          className={`
            w-full pl-10 pr-10 py-2 bg-gray-700 border border-gray-600 text-white rounded-md 
            focus:outline-hidden focus:ring-2 focus:ring-(--primary) focus:border-(--primary)
            placeholder-gray-400
            ${className}
          `}
          autoComplete="off"
        />

        <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
          {isLoading ? (
            <Loader2 className="h-4 w-4 text-gray-400 animate-spin" />
          ) : value ? (
            <button
              type="button"
              onClick={clearInput}
              className="text-gray-400 hover:text-gray-600"
              title={t("1169", "Clear input")}
            >
              <X className="h-4 w-4" />
            </button>
          ) : null}
        </div>
      </div>

      {/* Dropdown */}
      {isOpen && (results.length > 0 || isLoading) && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-gray-800 border border-gray-600 rounded-md shadow-lg max-h-60 overflow-auto"
        >
          {isLoading && results.length === 0 ? (
            <div className="px-4 py-3 text-sm text-gray-400 flex items-center">
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              {t("1170", "Searching cities...")}
            </div>
          ) : results.length > 0 ? (
            results.map((city, index) => (
              <button
                key={city.place_id}
                type="button"
                onClick={() => selectCity(city)}
                className={`
                  w-full px-4 py-3 text-left hover:bg-gray-700 focus:bg-gray-700 
                  focus:outline-hidden border-b border-gray-600 last:border-b-0
                  ${
                    selectedIndex === index
                      ? "bg-(--primary)/10 border-(--primary)/20"
                      : ""
                  }
                `}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start flex-1 min-w-0">
                    <MapPin className="h-4 w-4 text-gray-400 mr-2 shrink-0 mt-0.5" />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-white truncate">
                        {getDisplayText(city)}
                      </div>
                      <div className="text-xs text-gray-400 truncate">
                        {getSubtext(city)}
                      </div>
                    </div>
                  </div>
                  {getTypeIndicator(city) && (
                    <span className="ml-2 text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded-full shrink-0">
                      {getTypeIndicator(city)}
                    </span>
                  )}
                </div>
              </button>
            ))
          ) : (
            <div className="px-4 py-3 text-sm text-gray-400">
              {t("1171", "No cities found")}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CityAutocomplete;
